import { useCallback, useEffect } from 'react';
import { logUserAction } from '@/utils/analyticsUtils';
import { useToast } from '@/hooks/use-toast';
import { useSearchParams, useLocation } from 'react-router-dom';

/**
 * Hook for tracking user actions within the application
 * @param options Optional configuration for analytics tracking
 */
export const useAnalytics = (options?: { component?: string; pageView?: boolean }) => {
  const { toast } = useToast();
  const component = options?.component || null;
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Get UTM parameters for tracking
  const getUtmParams = () => {
    return {
      utm_source: searchParams.get("utm_source") || undefined,
      utm_medium: searchParams.get("utm_medium") || undefined,
      utm_campaign: searchParams.get("utm_campaign") || undefined
    };
  };

  /**
   * Track a page view
   */
  const trackPageView = useCallback((pageName: string) => {
    // Track the page view using the analytics utility
    logUserAction(
      'page_view', 
      component, 
      pageName, 
      { 
        page: pageName,
        ...getUtmParams()
      }
    );
  }, [component, searchParams]);

  /**
   * Track a user action (click, form submission, etc.)
   */
  const trackAction = useCallback((
    actionType: string,
    details?: Record<string, any>
  ) => {
    // Track the action using the analytics utility
    logUserAction(
      actionType,
      component,
      null,
      {
        ...getUtmParams(),
        ...(details || {})
      }
    );
  }, [component, searchParams]);

  /**
   * Track an error that occurred
   */
  const trackError = useCallback((
    errorType: string,
    errorDetails: Record<string, any>
  ) => {
    // Log the error to the console
    console.error(`Error in ${component || 'unknown component'}: ${errorType}`, errorDetails);
    
    // Track the error using the analytics utility
    logUserAction(
      'error',
      component,
      null,
      {
        error_type: errorType,
        ...getUtmParams(),
        ...errorDetails
      }
    );
    
    // Show a toast notification for the error
    toast({
      title: "An error occurred",
      description: errorDetails.message || errorType,
      variant: "destructive"
    });
  }, [component, toast, searchParams]);

  // If pageView is true, track a page view on mount
  useEffect(() => {
    if (options?.pageView) {
      const pagePath = location.pathname;
      trackPageView(pagePath);
      console.log(`Auto-tracked page view for: ${pagePath}`);
    }
  }, [options?.pageView, location.pathname, trackPageView]);

  return { trackPageView, trackAction, trackError };
};
